/**
 * Inspector-specific sync status bar - wraps `ActiveSyncStatus` with the
 * layout's visibility/dismiss behaviour: shown while syncs run (or
 * force-shown right after a trigger), auto-hidden 5s after completion,
 * and broadcasting `evop:sync-completed` for sibling freshness consumers.
 *
 * Moved out of `InspectorLayout.tsx` unchanged (pure structural move).
 */
import React, { useState, useEffect, useCallback } from 'react'
import ActiveSyncStatus from '../../sync/ActiveSyncStatus'
import useActiveSyncStatus from '../../../hooks/useActiveSyncStatus'

interface SyncStatusBarWrapperProps {
  fpCode: string
  forceShow: boolean
  onSyncComplete: () => void
  onActiveChange?: (active: boolean) => void
}

const SyncStatusBarWrapper: React.FC<SyncStatusBarWrapperProps> = ({
  fpCode,
  forceShow,
  onSyncComplete,
  onActiveChange
}) => {
  const [visible, setVisible] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const { operations, isLoading } = useActiveSyncStatus({
    fpCode,
    autoRefresh: true,
    onSyncComplete: (sync) => {
      // Broadcast for sibling freshness consumers (useSyncFreshness) so
      // chips + banner refresh without a second WebSocket / poll loop.
      window.dispatchEvent(new CustomEvent('evop:sync-completed', { detail: { fpCode } }))
      if (onSyncComplete) {
        onSyncComplete()
      }
      setTimeout(() => {
        setVisible(false)
        setDismissed(false)
      }, 5000)
    }
  })

  // Push isSyncing up so the alert stack can suppress the freshness row
  // without subscribing to its own poll loop.
  useEffect(() => {
    if (onActiveChange) {
      onActiveChange(operations.length > 0)
    }
  }, [operations.length, onActiveChange])

  // Show/hide logic
  useEffect(() => {
    if (forceShow && !dismissed) {
      setVisible(true)
    } else if (operations.length > 0 && !dismissed) {
      setVisible(true)
    } else if (operations.length === 0 && !forceShow) {
      const timer = setTimeout(() => {
        setVisible(false)
        setDismissed(false)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [operations.length, forceShow, dismissed])

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    setTimeout(() => setVisible(false), 300)
  }, [])

  return (
    <ActiveSyncStatus
      operations={operations}
      isLoading={isLoading}
      fpCode={fpCode}
      compact={true}
      visible={visible}
      onDismiss={handleDismiss}
    />
  )
}

export default SyncStatusBarWrapper
